import { Grid } from '@mui/material';
import { getLocalDate, Units } from '@surfline/web-common';
import { useMemo } from 'react';
import { format } from 'date-fns';
import type { LiveWindPoint } from 'types/liveWind';
import type { WindTimestep } from 'types/wind';
import { convertToMilliseconds } from 'utils/time';
import { useLiveWindModalStationIsOffline } from 'hooks/liveWind/useLiveWindModal';
import WindStationOffline from '../WindStationOffline';
import WindStats from '../WindStats';

interface WindStationTooltipProps {
  forecastedWind?: WindTimestep;
  isLoading: boolean;
  observedWind: LiveWindPoint;
  lastObservedTimestamp?: number;
  windSpeedUnits?: Units['windSpeed'];
  utcOffset: number;
}

const WindStatsTooltip: React.FC<WindStationTooltipProps> = ({
  forecastedWind = undefined,
  isLoading,
  observedWind,
  windSpeedUnits,
  lastObservedTimestamp = undefined,
  utcOffset,
}) => {
  const [, setIsStationOffline] = useLiveWindModalStationIsOffline();
  const currentTimestamp = Math.floor(+new Date());
  const isOnline = useMemo(() => {
    const isOnlineValue =
      observedWind &&
      (observedWind.sensorWind === true || observedWind.timestamp * 1000 > currentTimestamp);
    setIsStationOffline(!isOnlineValue);
    return isOnlineValue;
  }, [currentTimestamp, observedWind, setIsStationOffline]);
  const isPredicted = useMemo(
    () => observedWind && convertToMilliseconds(observedWind.timestamp) > currentTimestamp,
    [currentTimestamp, observedWind],
  );

  return (
    <Grid container data-testid="live-wind-stats-tooltip">
      <>
        <Grid item mobile={isLoading || forecastedWind ? 6 : 12}>
          {isLoading || isOnline ? (
            <WindStats
              wind={observedWind?.wind}
              loading={isLoading}
              windSpeedUnits={windSpeedUnits}
              isPredicted={isPredicted}
              extraText={
                observedWind
                  ? `Last observed at ${format(
                      getLocalDate(observedWind?.timestamp, observedWind?.utcOffset),
                      'h:mmaaa',
                    )}`
                  : undefined
              }
            />
          ) : (
            <WindStationOffline
              lastObservedTimestamp={lastObservedTimestamp}
              utcOffset={utcOffset}
            />
          )}
        </Grid>
        {(isLoading || forecastedWind) && (
          <Grid item mobile={6}>
            <WindStats
              wind={forecastedWind}
              loading={isLoading}
              windSpeedUnits={windSpeedUnits}
              isForecast
            />
          </Grid>
        )}
      </>
    </Grid>
  );
};

export default WindStatsTooltip;
